import React, { useState, useEffect } from "react";
import { ProjectMedia, PresignedUrlResponse } from "@/types";
import { fetchMediaPresignedUrl } from "@/services/api";
import { Spinner } from "@/components/ui/spinner";
import { ImageIcon } from "lucide-react";

interface ProjectMediaCarouselProps {
  projectId: string;
  projectMedia: ProjectMedia[];
  onImageClick?: (index: number) => void;
  noPaddingX?: boolean;
}

const ProjectMediaCarousel: React.FC<ProjectMediaCarouselProps> = ({
  projectId,
  projectMedia,
  onImageClick,
  noPaddingX = false,
}) => {
  const [presignedUrls, setPresignedUrls] = useState<
    Record<string, PresignedUrlResponse>
  >({});
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    if (!projectMedia || projectMedia.length === 0) return;

    const fetchPresignedUrls = async () => {
      const firstMedia = projectMedia[0]; // Only fetch for the first image initially

      if (firstMedia && !presignedUrls[firstMedia.uri]) {
        setLoadingImages((prev) => ({ ...prev, [firstMedia.uri]: true }));

        try {
          const urlData = await fetchMediaPresignedUrl(
            projectId,
            firstMedia.uri
          );
          if (urlData) {
            setPresignedUrls((prev) => ({
              ...prev,
              [firstMedia.uri]: urlData,
            }));
          }
        } catch (error) {
          console.error("Error fetching presigned URL:", error);
        } finally {
          setLoadingImages((prev) => ({ ...prev, [firstMedia.uri]: false }));
        }
      }
    };

    fetchPresignedUrls();
  }, [projectId, projectMedia, presignedUrls]);

  const getImageUrl = (media: ProjectMedia) => {
    if (presignedUrls[media.uri]) {
      return (
        presignedUrls[media.uri].thumbnailDownloadUrl ||
        presignedUrls[media.uri].downloadUrl
      );
    }
    return media.uri; // Fallback to original URI
  };

  // Event handlers to stop propagation
  const handleMouseEvent = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTouchEvent = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (!projectMedia || projectMedia.length === 0) {
    return null;
  }

  const firstMedia = projectMedia[0];
  const hasMultipleImages = projectMedia.length > 1;

  return (
    <div
      className={`w-full md:w-full ${
        noPaddingX ? "p-0" : "p-4"
      } flex items-center justify-center`}
      onClick={handleMouseEvent}
      onMouseDown={handleMouseEvent}
      onTouchStart={handleTouchEvent}
    >
      <div className="relative w-full h-40 cursor-pointer">
        <div
          className="h-full relative rounded-md overflow-hidden border border-gray-200"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onImageClick?.(0);
          }}
        >
          {loadingImages[firstMedia.uri] ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <Spinner className="h-8 w-8 text-gray-400" />
            </div>
          ) : (
            <img
              src={getImageUrl(firstMedia)}
              alt={firstMedia.fileName || "Project image"}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target.src !== firstMedia.uri) {
                  target.src = firstMedia.uri;
                }
              }}
            />
          )}
        </div>

        {hasMultipleImages && (
          <div
            className="absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-1 text-white text-xs flex items-center"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onImageClick?.(0);
            }}
          >
            <ImageIcon className="h-3 w-3 mr-1" />
            <span>{projectMedia.length}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectMediaCarousel;
